/** @jsxImportSource @emotion/react */
import React from 'react'
import { Trans } from 'react-i18next'
import { default_theme as theme } from '../emotion/theme'
import { jsx, css } from '@emotion/react'
import { get, map, size, filter, startsWith } from 'lodash'
import { Row, Col, Button } from 'react-bootstrap'
import { Separator } from './layout/Separator'
import { InlineInputField } from './form/InlineInputField'
import { StandaloneCheckboxField } from './form/CheckboxField'
import { SemanticDropdownStandalone } from './form/Dropdown'
import { FormikInputField } from './form/InputField'
import { FormikPhoneNumberInputField } from './form/PhoneNumberInputField'
import { FormikRadioGroupField } from './form/RadioGroupField'
import { FieldArray } from 'formik'
import { BlueLinkButton } from './layout/BlueLinkButton'
import { InlineIcon } from './layout/InlineIcon'
import { WhiteButton } from './layout/WhiteButton'
import CurrencyValue from './CurrencyValue'
import { PhoneNumber } from './PhoneNumber'
import { FreeMinutesDuration } from './FreeMinutesDuration'
import { Error } from './layout/Error'

const FORWARDING_LIST_TYPE_OPTIONS = [
  { value: 'phone_number', label: <Trans>Phone number</Trans> },
  { value: 'call_list', label: <Trans>Call list</Trans> },
]

const CALL_LIST_RING_STRATEGY_OPTIONS = [
  { value: 'hunt', label: <Trans>One at a time</Trans> },
  { value: 'ringall', label: <Trans>Simultaneous</Trans> },
]

const MAX_FORWARDING_DESTINATIONS = 5

export const VoiceCallPlanForwardingRow = ({
  clusterType,
  forwardingListType,
  onChangeType,
  isEnabled,
  onToggle,
  destinations,
  ringStrategy,
  callerIdType,
  onSave,
  forwardingValidation,
  getRateForNumber,
  subscription,
  customer,
  customer_phone_number,
  isSaving,
  isToggleSaving,
  isListTypeSaving,
  editing_as_customer,
  onBuyCredit,
}) => {
  const initialValues = {
    destinations,
    ring_strategy: ringStrategy,
    caller_id_type: callerIdType || 'caller',
  }

  const destinationQualifiesForFreeMinutes = (destination) => {
    const number = get(destination, 'number')
    return (
      size(
        filter(
          subscription.prefixes_qualifying_for_free_forwarding_minutes,
          (prefix) => startsWith(number, prefix)
        )
      ) > 0
    )
  }

  const renderDestinationRate = ({ destination, rate_object_for_number }) => {
    const number = get(destination, 'number')
    const qualifies_for_free_minutes =
      destinationQualifiesForFreeMinutes(destination)
    const show_rate =
      !qualifies_for_free_minutes ||
      subscription.remaining_monthly_free_voice_forwarding_minutes <= 0
    rate_object_for_number =
      show_rate && (rate_object_for_number || getRateForNumber(number))
    const rate = get(
      rate_object_for_number,
      'cost_per_minute_excluding_vat_euros'
    )
    const number_is_valid = number && number !== '00'

    return (
      <>
        {subscription.monthly_num_free_voice_forwarding_minutes > 0 &&
          !qualifies_for_free_minutes &&
          number_is_valid && (
            <div css={readonly_number_info_text_style}>
              <Separator variant="w10" />
              <Trans>Free minutes do not apply</Trans>
            </div>
          )}

        {show_rate && rate && (
          <div css={readonly_number_info_text_style}>
            <Separator variant="w10" />
            <CurrencyValue value={rate} />/<Trans>min.</Trans>
          </div>
        )}
      </>
    )
  }

  const tryRenderPhoneNumberInfos = (field_name) => {
    const info = get(forwardingValidation.infos, field_name, {})

    return (
      <>
        {info.valid === false && (
          <>
            <Separator variant="w20" />
            <Error>
              <Trans>You cannot forward to this number</Trans>
            </Error>
          </>
        )}
        {info.cost_per_minute_excluding_vat_euros && (
          <>
            <Separator variant="w20" />
            <div css={readonly_number_info_text_style}>
              <Separator variant="w10" />
              <CurrencyValue value={info.cost_per_minute_excluding_vat_euros} />
              /<Trans>min.</Trans>
            </div>
          </>
        )}
      </>
    )
  }

  const renderEditCallToFormFieldsSinglePhoneNumber = (formik_props) => {
    const caller_id_options = [
      { value: 'caller', label: <Trans>Caller's number</Trans> },
      {
        value: 'subscription_number',
        label: (
          <div css={my_number_readonly_style}>
            <Trans>My number</Trans>
            <Separator variant="w2" />
            (<PhoneNumber phone_number={get(customer_phone_number, 'number')} />)
          </div>
        ),
      },
    ]
    return (
      <div>
        <FieldArray
          name={'destinations'}
          render={(arrayHelpers) => (
            <>
              <Row>
                <Col css={multivalue_col_style}>
                  <div css={inline_edit_form_label_style}>
                    <Trans>Phone number</Trans>
                  </div>
                  <FormikPhoneNumberInputField
                    name="destinations.0.number"
                    formik_props={formik_props}
                    onBlur={(value) =>
                      forwardingValidation.onBlur(
                        value,
                        'destinations.0.number',
                        formik_props
                      )
                    }
                    onChange={(value) =>
                      forwardingValidation.onChange(
                        value,
                        'destinations.0.number',
                        formik_props
                      )
                    }
                    onlyCountries={get(
                      subscription,
                      'available_country_codes_for_call_forwarding',
                      ['nl']
                    )}
                  />
                  {tryRenderPhoneNumberInfos('destinations.0.number')}
                </Col>
              </Row>

              <Separator variant="w30" />

              <Row>
                <Col css={multivalue_col_style}>
                  <div css={inline_edit_form_label_style}>
                    <Trans>Ring for</Trans>
                  </div>
                  <FormikInputField
                    name="destinations.0.ring_duration_seconds"
                    extra_css={ring_duration_seconds_input_style}
                    formik_props={formik_props}
                  />
                  <Separator variant="w10" />
                  <Trans>seconds</Trans>
                </Col>
              </Row>

              <Separator variant="h30" />

              <Row>
                <Col css={multivalue_col_style}>
                  <div css={inline_edit_form_label_style}>
                    <Trans>Caller ID shows</Trans>
                  </div>
                  <div css={caller_id_type_style}>
                    <FormikRadioGroupField
                      formik_props={formik_props}
                      auto_scale={true}
                      name="caller_id_type"
                      options={caller_id_options}
                    />
                  </div>
                </Col>
              </Row>
              <Separator variant="h20" />
            </>
          )}
        />
      </div>
    )
  }

  const renderEditCallToFormFieldsCallList = (formik_props) => {
    const caller_id_options = [
      { value: 'caller', label: <Trans>Caller's number</Trans> },
      {
        value: 'subscription_number',
        label: (
          <div css={my_number_readonly_style}>
            <Trans>My number</Trans>
            <Separator variant="w2" />
            (<PhoneNumber phone_number={get(customer_phone_number, 'number')} />)
          </div>
        ),
      },
    ]

    return (
      <div>
        <FormikRadioGroupField
          label="Call"
          auto_scale={false}
          name="ring_strategy"
          formik_props={formik_props}
          options={CALL_LIST_RING_STRATEGY_OPTIONS}
        />

        <Separator variant="h20" />

        <FieldArray
          name={'destinations'}
          render={(arrayHelpers) => (
            <>
              {map(
                get(formik_props, ['values', 'destinations'], []),
                (destination, index) => {
                  const phone_number_input_field_name = `destinations.${index}.number`
                  return (
                    <div key={index}>
                      <div css={forwarding_phone_number_collapsed_style}>
                        <div css={forwarding_phone_number_collapsed_label_style}>
                          {index + 1}.
                          <Separator variant="w10" />
                          <FormikPhoneNumberInputField
                            name={phone_number_input_field_name}
                            formik_props={formik_props}
                            onBlur={(value) =>
                              forwardingValidation.onBlur(
                                value,
                                phone_number_input_field_name,
                                formik_props
                              )
                            }
                            onChange={(value) =>
                              forwardingValidation.onChange(
                                value,
                                phone_number_input_field_name,
                                formik_props
                              )
                            }
                            onlyCountries={get(
                              subscription,
                              'available_country_codes_for_call_forwarding',
                              ['nl']
                            )}
                          />
                          {tryRenderPhoneNumberInfos(
                            phone_number_input_field_name
                          )}
                          <Separator variant="w20" />
                          <Trans>Ring for</Trans>
                          <Separator variant="w10" />
                          <FormikInputField
                            name={`destinations.${index}.ring_duration_seconds`}
                            extra_css={ring_duration_seconds_input_style}
                            formik_props={formik_props}
                          />
                          <Separator variant="w10" />
                          <Trans>seconds</Trans>
                        </div>

                        <div css={forwarding_phone_number_collapsed_actions_style}>
                          <InlineIcon
                            icon_name="remove"
                            onClick={() => arrayHelpers.remove(index)}
                          />
                        </div>
                      </div>

                      <Separator variant="h10" />
                    </div>
                  )
                }
              )}

              {size(get(formik_props.values, 'destinations')) <
                MAX_FORWARDING_DESTINATIONS && (
                <BlueLinkButton
                  onClick={() => {
                    const new_index = size(get(formik_props.values, 'destinations'))
                    // Use canonical structure for a new destination
                    try {
                      // Lazy import to avoid circular deps at module load
                      const { customerPhoneNumberList } = require('../actions/customer_phone_number')
                      arrayHelpers.insert(
                        new_index,
                        customerPhoneNumberList.getEmptyDestination({ customer_phone_number })
                      )
                    } catch (e) {
                      // Fallback minimal shape
                      arrayHelpers.insert(new_index, { number: '', ring_duration_seconds: 30 })
                    }
                  }}
                >
                  <InlineIcon icon_name="add_no_circle" variant="blue" />
                  <Trans
                    i18nKey="voice_number_call_plan__add_destination"
                    values={{ max: MAX_FORWARDING_DESTINATIONS }}
                    components={[<span>{MAX_FORWARDING_DESTINATIONS}</span>]}
                    defaults="Add destination (max <0>{{max}}</0>)"
                  />
                </BlueLinkButton>
              )}

              <Separator variant="h30" />

              <Row>
                <Col css={multivalue_col_style}>
                  <div css={inline_edit_form_label_style}>
                    <Trans>Caller ID shows</Trans>
                  </div>
                  <div css={caller_id_type_style}>
                    <FormikRadioGroupField
                      formik_props={formik_props}
                      auto_scale={true}
                      name="caller_id_type"
                      options={caller_id_options}
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}
        />
        <Separator variant="20px" />
      </div>
    )
  }

  const renderReadonlySendCallToRowCredit = (destinations) => {
    const atLeastOneDestinationQualifiesForFreeMinutes =
      size(filter(destinations, destinationQualifiesForFreeMinutes)) > 0
    const atLeastOneDestinationDoesntQualifyForFreeMinutes =
      size(filter(destinations, destinationQualifiesForFreeMinutes)) <
      size(destinations)
    const credits_excluding_vat_euros = get(
      customer,
      'credits_excluding_vat_euros',
      null
    )
    const credits_style =
      credits_excluding_vat_euros > 0
        ? credit_button_available_style
        : credit_button_empty_style
    const show_topup =
      editing_as_customer &&
      (atLeastOneDestinationDoesntQualifyForFreeMinutes ||
        subscription.remaining_monthly_free_voice_forwarding_minutes <= 0) &&
      credits_excluding_vat_euros <= 0
    const show_credits_badge =
      atLeastOneDestinationDoesntQualifyForFreeMinutes ||
      subscription.remaining_monthly_free_voice_forwarding_minutes <= 0

    if (!isEnabled) {
      return null
    }

    return (
      <div css={buy_credit_row_style}>
        {show_topup && (
          <div css={no_free_minutes_warning_style}>
            <WhiteButton extra_css={buy_credit_button_style} onClick={onBuyCredit} auto_disable={false}>
              <Trans>Buy Credit</Trans>
            </WhiteButton>
          </div>
        )}

        <Separator variant="w10" />

        {show_credits_badge && (
          <Button
            variant="success"
            css={[credit_button_common_style, credits_style]}
            onClick={onBuyCredit}
            auto_disable={false}
          >
            <CurrencyValue value={credits_excluding_vat_euros} />
          </Button>
        )}

        {atLeastOneDestinationQualifiesForFreeMinutes && (
          <>
            <Separator variant="w10" />
            <FreeMinutesDuration
              container_style={free_minutes_style}
              minutes={subscription.remaining_monthly_free_voice_forwarding_minutes}
            />
          </>
        )}
      </div>
    )
  }

  const renderReadonlySendCallToRow = () => {
    return (
      <>
        <Trans>Send call to</Trans>
        <Separator variant="w10" />
        <SemanticDropdownStandalone
          options={FORWARDING_LIST_TYPE_OPTIONS}
          disabled={!isEnabled}
          default_value={forwardingListType}
          on_change={(value) => onChangeType(value)}
          is_saving={isListTypeSaving}
          allowAdditions={false}
        />
        <Separator variant="w20" />
        <div css={single_forward_phone_number_readonly_style}>
          {isEnabled && (
            <div css={call_forwarding_destinations_readonly_list_style}>
              {map(destinations, (destination, index) => {
                const number = get(destination, 'number')
                const number_is_valid = number && number !== '00'

                return (
                  <div key={index}>
                    {index > 0 && <Separator variant="h10" />}
                    <div css={call_forwarding_destinations_readonly_row_style}>
                      <div css={call_forwarding_destinations_readonly_item_style}>
                        {number_is_valid && (
                          <PhoneNumber phone_number={number} />
                        )}
                        {renderDestinationRate({ destination })}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </>
    )
  }

  const renderEditCallToFormFields = (formik_props) => {
    return (
      <div>
        <Row>
          <Col css={multivalue_col_style}>
            <div css={inline_edit_form_label_style}>
              <Trans>Send call to</Trans>
            </div>
            <SemanticDropdownStandalone
              options={FORWARDING_LIST_TYPE_OPTIONS}
              default_value={forwardingListType}
              on_change={onChangeType}
              allowAdditions={false}
            />
          </Col>
        </Row>

        <Separator variant="h30" />

        {forwardingListType === 'phone_number' &&
          renderEditCallToFormFieldsSinglePhoneNumber(formik_props)}
        {forwardingListType === 'call_list' &&
          renderEditCallToFormFieldsCallList(formik_props)}
      </div>
    )
  }

  const has_valid_forward_to_phone_number_info =
    !get(destinations, [0, '_default_auto_created'])

  return (
    <div css={[row_style, white_row_style, isEnabled ? null : disabled_row_style]}>
      <div css={toggle_column_style}>
        <div css={toggle_checkbox_cell_style}>
          <StandaloneCheckboxField
            onChange={() => onToggle()}
            label=""
            is_saving={isToggleSaving}
            variant="large"
            checked={isEnabled}
          />
        </div>
      </div>
      <div css={value_column_style}>
        <div css={multivalue_col_style}>
          <InlineInputField
            startInEditingMode={isEnabled && !has_valid_forward_to_phone_number_info}
            formButtonStyle="buttons"
            readOnlyValue={<div css={readonly_row_style}>{renderReadonlySendCallToRow()}</div>}
            renderRightSide={() => renderReadonlySendCallToRowCredit(destinations)}
            formInitialValues={initialValues}
            canEdit={isEnabled}
            onSave={onSave}
            is_saving={isSaving}
            renderFormFields={renderEditCallToFormFields}
          />
        </div>
      </div>
    </div>
  )
}

const row_style = css`
  display: flex;
  flex-wrap: wrap;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 15px;
  width: 100%;
  margin-left: 0px;
  margin-right: 30px;
  align-items: center;
  min-height: 68px;
`

const white_row_style = css`
  background-color: #fff;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  align-items: center;
  line-height: 18px;
`

const disabled_row_style = css`
  color: ${theme.colors.dark_grey};
`

const toggle_column_style = css`
  display: flex;
  margin-right: 15px;
  min-width: 40px;
  max-width: 40px;
  align-self: flex-start;
  margin-top: 8px;
  justify-content: center;
  width: 100%;
`

const toggle_checkbox_cell_style = css`
  display: flex;
  flex-grow: 1;
`

const value_column_style = css`
  padding-bottom: 15px;
  flex-grow: 1;
`

const multivalue_col_style = css`
  display: flex;
  align-items: center;
  min-width: 150px;
  width: 100%;
`

const inline_edit_form_label_style = css`
  width: 150px;
`

const single_forward_phone_number_readonly_style = css`
  font-weight: bold;
`

const call_forwarding_destinations_readonly_list_style = css``

const call_forwarding_destinations_readonly_row_style = css`
  display: flex;
  justify-content: space-between;
`

const call_forwarding_destinations_readonly_item_style = css`
  display: flex;
`

const ring_duration_seconds_input_style = css`
  width: 50px;
  max-width: 50px;
`

const caller_id_type_style = css`
  width: 500px;
`

const my_number_readonly_style = css`
  display: flex;
`

const forwarding_phone_number_collapsed_style = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  background-color: ${theme.colors.nearly_white};
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 4px;
  padding-bottom: 4px;
`

const forwarding_phone_number_collapsed_label_style = css`
  display: flex;
  align-items: center;
`

const forwarding_phone_number_collapsed_actions_style = css`
  display: flex;
  margin-left: 10px;
  margin-top: 5px;
`

const readonly_row_style = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
`

const readonly_number_info_text_style = css`
  color: ${theme.colors.dark_grey};
  font-weight: normal;
  display: flex;
`

const buy_credit_row_style = css`
  display: flex;
  align-items: center;
`

const credit_button_common_style = css`
  color: #fff;
  border-radius: 20px;
  font-size: 13px;
  padding: 3px 9px 4px 8px;
`

const credit_button_available_style = css`
  background-color: ${theme.colors.dark_green};
`

const credit_button_empty_style = css`
  background-color: ${theme.colors.primary_red};
  border-color: ${theme.colors.primary_red};

  &:hover {
    border-color: ${theme.colors.primary_red};
    background-color: ${theme.colors.primary_red};
  }
`

const free_minutes_style = css`
  font-size: 13px;
  padding: 3px 9px 5px 8px;
`

const no_free_minutes_warning_style = css`
  color: ${theme.colors.warning_red};
  display: flex;
  align-items: center;
`

const buy_credit_button_style = css`
  font-size: 14px;
  height: 32px;
`

// No extra styles required; using shared components for errors


